import { getChunks } from "@codemirror/merge";
import { StateEffect } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { DocumentAiState } from "pages/DevelopWorkspace/workspace.types";

import {
  Callbacks,
  aiAssistantConfigCompartment,
  aiAssistantConfigFacet,
  focusAiAssistant,
  setAiAssistantState,
} from "../extensions/aiAssistantWidget/aiAssistantWidget";
import { setHighlightEffect } from "../extensions/highlightStatement";

export function applyInitialAiState(
  view: EditorView,
  aiState: DocumentAiState | null
): void {
  if (!aiState) return;

  const effectsNow: StateEffect<unknown>[] = [setAiAssistantState.of(aiState)];
  const [from, to] = aiState.pos;
  if (from !== to) {
    effectsNow.push(
      setHighlightEffect.of({
        from: from || 0,
        to: to || 0,
      })
    );
  }

  view.dispatch({ effects: effectsNow });
  view.dispatch({
    effects: EditorView.scrollIntoView(aiState.widgetPosition, {
      y: "center",
      x: "start",
    }),
  });
  view.dispatch({ effects: [focusAiAssistant.of(null)] });

  const isTextArea = (el: Element | null): el is HTMLTextAreaElement =>
    !!el && el instanceof HTMLTextAreaElement;

  requestAnimationFrame(() => {
    const textareaCandidate = view.dom.querySelector(
      ".cm-ai-assistant-textarea"
    );
    if (isTextArea(textareaCandidate) && !textareaCandidate.disabled) {
      textareaCandidate.focus();
      const end = textareaCandidate.value.length;
      textareaCandidate.setSelectionRange(end, end);
    }
  });
}

export function configureAiAssistant(
  view: EditorView,
  params: {
    documentId: string;
    callbacks: Callbacks;
    aiState: DocumentAiState | null;
  }
): StateEffect<unknown>[] {
  const effects: StateEffect<unknown>[] = [];
  effects.push(
    aiAssistantConfigCompartment.reconfigure(
      aiAssistantConfigFacet.of({
        documentId: params.documentId,
        callbacks: params.callbacks,
      })
    )
  );

  if (params.aiState) {
    effects.push(setAiAssistantState.of(params.aiState));
    const [from, to] = params.aiState.pos;
    if (from !== to) {
      effects.push(
        setHighlightEffect.of({
          from: from || 0,
          to: to || 0,
        })
      );
    } else {
      effects.push(setHighlightEffect.of(null));
    }
  } else {
    effects.push(setAiAssistantState.of(null));
    effects.push(setHighlightEffect.of(null));
  }

  return effects;
}

export function toggleSingleChunkClass(
  view: EditorView,
  host: HTMLElement
): void {
  const info = getChunks(view.state);
  const count = info?.chunks ? info.chunks.length : 0;
  const isSingleChunkInitially = count === 1;
  host.classList.toggle("cm-single-chunk-merge", isSingleChunkInitially);
}
